import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, Card, Modal, Input, Select, Textarea, PrimaryButton, GhostButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Users2, Plus, ArrowLeft, UserPlus, Crown, LogIn, Check, X, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Teams() {
  const navigate = useNavigate();
  const [all, setAll] = useState(null);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "", city: "", skill_level: "amateur", description: "" });

  const load = () => {
    api.get("/teams").then((r) => setAll(r.data)).catch(() => setAll([]));
    api.get("/me/teams").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!all) return <Spinner />;

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/teams", form); toast.success("Team created"); setOpen(false); navigate(`/app/teams/${data.id}`); }
    catch (err) { toast.error(apiError(err)); }
  };
  const respond = async (mid, accept) => { try { await api.post(`/team-members/${mid}/respond`, { accept }); toast.success(accept ? "Joined" : "Declined"); load(); } catch (e) { toast.error(apiError(e)); } };
  const join = async (tid) => { try { await api.post(`/teams/${tid}/join`); toast.success("Join request sent"); load(); } catch (e) { toast.error(apiError(e)); } };

  const invites = mine.filter((t) => t.my_status === "invited");

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/play")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Play</button>
      <PageHeader title="Teams" subtitle="Create a team, invite players, find opponents" action={
        <div className="flex gap-2">
          <GhostButton data-testid="find-players-btn" onClick={() => navigate("/app/players")}><UserPlus className="h-4 w-4" /> Find players</GhostButton>
          <PrimaryButton data-testid="create-team-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Create team</PrimaryButton>
        </div>} />

      {invites.length > 0 && (
        <Card className="mb-4 border-primary/40">
          <h3 className="mb-3 font-display text-lg font-semibold">Your invitations</h3>
          {invites.map((t) => (
            <div key={t.member_id} className="flex items-center justify-between border-b border-border/50 py-2 last:border-0">
              <span className="text-sm"><strong>{t.name}</strong> · {t.sport}</span>
              <div className="flex gap-2">
                <button data-testid={`accept-invite-${t.member_id}`} onClick={() => respond(t.member_id, true)} className="rounded-lg bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/25"><Check className="h-4 w-4" /></button>
                <button onClick={() => respond(t.member_id, false)} className="rounded-lg bg-rose-500/15 p-1.5 text-rose-300 hover:bg-rose-500/25"><X className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {mine.filter((t) => t.my_status === "active").length > 0 && (
        <>
          <h3 className="mb-3 font-display text-xl font-semibold">My teams</h3>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {mine.filter((t) => t.my_status === "active").map((t) => <TeamCard key={t.id} t={t} onOpen={() => navigate(`/app/teams/${t.id}`)} badge={t.my_role === "captain" ? "captain" : null} />)}
          </div>
        </>
      )}

      <h3 className="mb-3 font-display text-xl font-semibold">Discover teams</h3>
      {all.length === 0 ? <EmptyState icon={Users2} title="No teams yet" text="Be the first to create a team." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {all.map((t) => <TeamCard key={t.id} t={t} onOpen={() => navigate(`/app/teams/${t.id}`)} onJoin={() => join(t.id)} />)}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create a team" wide>
        <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
          <Input label="Team name" testId="team-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sm:col-span-2" />
          <Input label="Sport" testId="team-sport" required value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} />
          <Input label="City" testId="team-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Select label="Skill level" testId="team-skill" value={form.skill_level} onChange={(e) => setForm({ ...form, skill_level: e.target.value })}>
            <option value="amateur">Amateur</option><option value="intermediate">Intermediate</option><option value="competitive">Competitive</option>
          </Select>
          <Textarea label="Description" testId="team-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sm:col-span-2" />
          <div className="flex justify-end gap-2 sm:col-span-2"><GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-team-btn" type="submit">Create</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}

function TeamCard({ t, onOpen, onJoin, badge }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50">
      <div className="flex items-start justify-between">
        <button onClick={onOpen} className="text-left font-display text-lg font-semibold hover:text-primary">{t.name}</button>
        {badge && <span className="flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-mono uppercase text-primary"><Crown className="h-3 w-3" /> {badge}</span>}
      </div>
      <p className="mt-1 text-sm capitalize text-primary">{t.sport} · {t.skill_level}</p>
      {t.city && <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {t.city}</p>}
      {onJoin && !t.my_status && <GhostButton className="mt-3 w-full py-2" onClick={onJoin}><LogIn className="h-4 w-4" /> Request to join</GhostButton>}
    </div>
  );
}

export function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");

  const load = () => api.get(`/teams/${id}`).then((r) => setData(r.data)).catch(() => navigate("/app/teams"));
  useEffect(() => { load(); }, [id]);
  if (!data) return <Spinner />;
  const { team, members } = data;
  const isCaptain = team.captain_id === user?.id || hasRole("admin");

  const invite = async (e) => { e.preventDefault(); try { await api.post(`/teams/${id}/invite`, { email }); toast.success("Invitation sent"); setEmail(""); load(); } catch (err) { toast.error(apiError(err)); } };
  const remove = async (mid) => { try { await api.delete(`/teams/${id}/members/${mid}`); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/teams")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All teams</button>
      <PageHeader title={team.name} subtitle={`${team.sport} · ${team.skill_level}${team.city ? ` · ${team.city}` : ""}`} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h3 className="mb-3 font-display text-lg font-semibold">Roster ({members.filter((m) => m.status === "active").length})</h3>
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">{(m.user_name || "?")[0].toUpperCase()}</div>
                  <div><p className="text-sm font-medium">{m.user_name}</p><p className="text-xs capitalize text-muted-foreground">{m.role}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={m.status === "active" ? "confirmed" : m.status === "requested" ? "pending" : m.status === "invited" ? "sent" : "rejected"} />
                  {isCaptain && m.role !== "captain" && <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 font-display text-lg font-semibold">Captain</h3>
          <p className="flex items-center gap-2 text-sm"><Crown className="h-4 w-4 text-primary" /> {team.captain_name}</p>
          {team.description && <p className="mt-3 text-sm text-muted-foreground">{team.description}</p>}
          {isCaptain && (
            <form onSubmit={invite} className="mt-5 border-t border-border pt-4">
              <Input label="Invite player by email" testId="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              <PrimaryButton data-testid="send-invite-btn" type="submit" className="mt-3 w-full"><UserPlus className="h-4 w-4" /> Send invite</PrimaryButton>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
